import React, { useState, useEffect, useCallback } from 'react';

const STEP_CONTENT = {
  0: {
    title:  'This is not a prompt-based AI generator',
    body: [
      'VISINVOKE works differently.',
      'Instead of guessing at prompts, you build results using structured controls.',
      'Every choice directly shapes the output, giving you consistent, repeatable results without trial and error.',
      'Start simple \u2192 generate \u2192 adjust \u2192 refine.',
    ],
    button: 'Start Building Now',
  },
  1: {
    title:  null,
    body: [
      'Try changing just one element and generate again.',
      'This helps you understand how each selection affects the result.',
    ],
    button: 'Got it',
  },
  2: {
    title:  null,
    body: [
      'You can combine changes.',
      'Try adding something new, not just replacing existing selections.',
    ],
    button: 'Got it',
  },
  3: {
    title:  null,
    body: [
      'You are now creating variations.',
      'Subscriptions unlock continued refinement and extended builds.',
    ],
    button: 'Got it',
  },
};

function hasEverGenerated() {
  try { return localStorage.getItem('hasGeneratedImage') === 'true'; } catch (e) { return false; }
}

export default function OnboardingOverlay() {
  const [visible, setVisible] = useState(false);
  const [step,    setStep]    = useState(0);
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    const hasGenerated = hasEverGenerated();

    if (!hasGenerated) {
      // No generation ever — purge any stale progression, force step 0
      try { localStorage.removeItem('onboarding_local_step'); } catch (e) {}
      setStep(0);
      setVisible(true);
      return;
    }

    // Has generated — ONLY show step 1+ if onboarding_local_step explicitly exists
    const localStepRaw = localStorage.getItem('onboarding_local_step');
    if (!localStepRaw) return; // not initialised yet — show nothing

    const localStep = parseInt(localStepRaw, 10);
    if (localStep >= 1 && localStep <= 3 && STEP_CONTENT[localStep]) {
      setStep(localStep);
      setVisible(true);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    if (!hasEverGenerated()) {
      // No generation — do nothing, overlay returns on next load
      return;
    }
    const next = step + 1;
    if (next <= 3) {
      try { localStorage.setItem('onboarding_local_step', String(next)); } catch (e) {}
    } else {
      try { localStorage.removeItem('onboarding_local_step'); } catch (e) {}
    }
  }, [step]);

  if (!visible) return null;

  const content = STEP_CONTENT[step];
  if (!content) return null;

  return (
    <div style={ov.backdrop}>
      <div style={ov.modal}>
        {content.title && <div style={ov.title}>{content.title}</div>}
        <div style={ov.body}>
          {content.body.map((line, i) => <p key={i} style={ov.para}>{line}</p>)}
        </div>
        <label style={ov.checkLabel}>
          <input
            type="checkbox"
            checked={disable}
            onChange={e => setDisable(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Don't show tips again
        </label>
        <button style={ov.btn} onClick={handleDismiss}>{content.button}</button>
      </div>
    </div>
  );
}

const ov = {
  backdrop: {
    position:       'fixed',
    inset:          0,
    zIndex:         10000,
    background:     'rgba(0,0,0,0.72)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        24,
  },
  modal: {
    background:    '#0f1e35',
    border:        '1px solid rgba(167,139,250,0.3)',
    borderRadius:  12,
    padding:       '32px 36px',
    maxWidth:      480,
    width:         '100%',
    boxShadow:     '0 24px 60px rgba(0,0,0,0.6)',
    display:       'flex',
    flexDirection: 'column',
    gap:           16,
  },
  title: {
    fontSize:   20,
    fontWeight: 800,
    color:      '#ffffff',
    lineHeight: 1.3,
  },
  body: {
    display:       'flex',
    flexDirection: 'column',
    gap:           10,
  },
  para: {
    fontSize:   15,
    color:      'rgba(255,255,255,0.78)',
    lineHeight: 1.6,
    margin:     0,
  },
  checkLabel: {
    display:    'flex',
    alignItems: 'center',
    fontSize:   13,
    color:      'rgba(255,255,255,0.5)',
    cursor:     'pointer',
  },
  btn: {
    background:   'linear-gradient(135deg,#7c3aed,#8b5cf6,#a855f7)',
    color:        '#fff',
    border:       'none',
    borderRadius: 8,
    padding:      '12px 0',
    fontWeight:   700,
    fontSize:     15,
    cursor:       'pointer',
    marginTop:    4,
    width:        '100%',
  },
};
